import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import { Label } from '@shadcn/components/ui/label';
import { Badge } from '@shadcn/components/ui/badge';
import { updateMarketplaceDisplayNames } from '../AmazonSellerConfig.service';
import { toast } from 'react-toastify';
import { EditIcon, GlobeIcon, DollarSignIcon, LanguagesIcon } from 'lucide-react';

export function MarketplaceDisplayNameDialog({ open, onOpenChange, config, onSuccess }) {
  const [marketplaces, setMarketplaces] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config && config.marketplaces) {
      
      const initializedMarketplaces = config.marketplaces.map(marketplace => ({
        ...marketplace,
        displayName: marketplace.displayName
      }));
      setMarketplaces(initializedMarketplaces);
      setHasChanges(false);
    }
  }, [config]);

  const handleDisplayNameChange = (index, value) => {
    const updatedMarketplaces = [...marketplaces];
    updatedMarketplaces[index].displayName = value;
    setMarketplaces(updatedMarketplaces);
    
    // Check if there are actual changes compared to original names
    const hasRealChanges = updatedMarketplaces.some((mp, idx) => 
      mp.displayName !== config.marketplaces[idx].name
    );
    setHasChanges(hasRealChanges);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      
      const payload = marketplaces.map(marketplace => ({
        marketplaceId: marketplace.marketplaceId,
        displayName: marketplace.displayName
      }));

      console.log("config", config)
      await updateMarketplaceDisplayNames(config._id, payload);
      toast.success('Marketplace display names updated successfully');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update marketplace display names:', error);
      toast.error(error.response?.body?.message || 'Failed to update marketplace display names');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetToOriginal = () => {
    const resetMarketplaces = marketplaces.map((marketplace, index) => ({
      ...marketplace,
      displayName: config.marketplaces[index].name
    }));
    setMarketplaces(resetMarketplaces);
    setHasChanges(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <i className="bx bxl-amazon text-orange-500 text-xl"></i>
            Edit Marketplace Display Names
            <Badge variant="outline" className="ml-2">
              {config?.storeKey}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            
            
            {marketplaces.map((marketplace, index) => (
              <div key={marketplace.marketplaceId} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GlobeIcon className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">{marketplace.name}</span>
                    <Badge variant="secondary">{marketplace.countryCode}</Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <DollarSignIcon className="h-3 w-3" />
                    <span>{marketplace.defaultCurrencyCode}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <LanguagesIcon className="h-3 w-3" />
                    <span>{marketplace.defaultLanguageCode}</span>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 truncate">
                  {marketplace.domainName}
                </div>
                
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label htmlFor={`displayName-${index}`} className="text-sm font-medium mb-0">
                    Display Name
                  </Label>
                  <div className="col-span-3 flex gap-2">
                    <Input
                      id={`displayName-${index}`}
                      value={marketplace?.displayName}
                      onChange={(e) => handleDisplayNameChange(index, e.target.value)}
                      placeholder="Enter custom display name"
                      className="flex-1"
                    />
                    {marketplace?.displayName !== config?.marketplaces[index].name && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDisplayNameChange(index, config.marketplaces[index].name)}
                        className="px-2"
                      >
                        <i className="bx bx-reset"></i>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter className="flex justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetToOriginal}
                disabled={!hasChanges || isSubmitting}
              >
                Reset All
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!hasChanges || isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}